import React, { useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    InputAdornment,
    IconButton,
    CircularProgress
} from '@mui/material';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { LoginSchema } from "../../../Validation/ValidationRule";
import { useDispatch, useSelector } from 'react-redux';
import { login } from '../../../features/auth/authSlice';
import { breakpoints } from '../../../theme/theme';
import styles from '../../../styles/login/rightSection.module.scss';
import LegalDocumentLink from '../../../components/consent/LegalDocumentLink';
import consentStyles from '../../../styles/consent/legalConsent.module.scss';
import DEFAULT_ROUTES from '../../../constants/routes';
import { CONSENT_COPY } from '../../../constants/consentCopy';


const LoginForm = ({ onForgotPassword }) => {
    const { loading } = useSelector((state) => state.auth);
    const dispatch = useDispatch();
    const [showPassword, setShowPassword] = useState(false);

    const {
        handleSubmit,
        control,
        formState: { errors },
    } = useForm({
        mode: "onChange",
        resolver: zodResolver(LoginSchema),
        defaultValues: { email: "", password: "" },
    });

    const onSubmit = async (data) => {
        dispatch(login(data.email, data.password));
    };

    function handleTogglePasswordVisibility() {
        setShowPassword(prev => !prev);
    }

    return (
        <>
            <Box className={styles['form-header']}>
                <h6 className={styles['form-title']}>
                    Welcome back
                </h6>
                <p className={styles['form-subtitle']}>
                    Sign in to your account to continue
                </p>
            </Box>
            <Box
                component="form"
                onSubmit={handleSubmit(onSubmit)}
                className={styles['form']}
            >
                <Box className={styles['form-field']}>
                    <h6
                        htmlFor="email"
                        className={styles['field-label']}
                    >
                        Email address
                    </h6>

                    <Controller
                        name="email"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                id="email"
                                type="email"
                                placeholder="Enter your email"
                                variant="outlined"
                                fullWidth
                                size="small"
                                error={!!errors.email}
                                helperText={errors.email?.message}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '0.5rem',
                                        bgcolor: '#fff',
                                        fontSize: '1rem',
                                        color: '#0f172a',
                                        transition: 'all 0.2s',
                                        boxShadow: 'none',
                                        border: 'none',
                                        '& fieldset': {
                                            borderRadius: '0.5rem',
                                            borderColor: '#CBD5E1',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#3b82f6',
                                            borderRadius: '0.5rem',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#3b82f6',
                                            borderWidth: 2,
                                            borderRadius: '0.5rem',
                                        },
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#CBD5E1',
                                        borderWidth: 1,
                                        borderRadius: '0.5rem',
                                        transition: 'border-color 0.2s, border-width 0.2s, border-radius 0.2s',
                                    },
                                    '& .MuiOutlinedInput-input': {
                                        height: '1.2rem',
                                        [`@media (min-width:${breakpoints.xl}px)`]: {
                                            height: '1.625rem',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        padding: '12px 16px',
                                        backgroundColor: '#fff',
                                        fontSize: '0.7rem',
                                        [`@media (min-width:${breakpoints.xl}px)`]: {
                                            fontSize: '1rem',
                                        },
                                        
                                        color: '#0f172a',
                                        lineHeight: '1.5rem',
                                        fontWeight: 400,
                                        transition: 'all 0.2s',
                                    },
                                    '& .MuiInputBase-input::placeholder': {
                                        color: '#64748b',
                                        opacity: 1,
                                        fontSize: '13px',
                                        lineHeight: '1rem',
                                        fontWeight: 400,
                                    },  
                                    '& .MuiFormHelperText-root': {
                                        fontSize: '0.7rem',
                                        lineHeight: '1rem',
                                        color: '#ef4444',
                                    },
                                }}
                            />
                        )}
                    />

                </Box>
                <Box className={styles['form-field']}>
                    <h6
                        htmlFor="password"
                        className={styles['field-label']}
                    >
                        Password
                    </h6>
                    <Controller
                        name="password"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                id="password"
                                type={showPassword ? 'text' : 'password'}
                                placeholder="Enter your password"
                                variant="outlined"
                                fullWidth
                                size="small"
                                error={!!errors.password}
                                helperText={errors.password?.message}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '0.5rem 0 0 0.5rem',
                                        bgcolor: '#fff',
                                        fontSize: '1rem',
                                        color: '#0f172a',
                                        transition: 'all 0.2s',
                                        boxShadow: 'none',
                                        border: 'none',
                                        '& fieldset': {
                                            borderRadius: '0.5rem',
                                            borderColor: '#CBD5E1',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#3b82f6',
                                            borderRadius: '0.5rem',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#3b82f6',
                                            borderWidth: 2,
                                            borderRadius: '0.5rem',
                                        },
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#CBD5E1',
                                        borderWidth: 1,
                                        borderRadius: '0.5rem',
                                        transition: 'border-color 0.2s, border-width 0.2s, border-radius 0.2s',
                                    },
                                    '& .MuiOutlinedInput-input': {
                                        height: '1.2rem',
                                        [`@media (min-width:${breakpoints.xl}px)`]: {
                                            height: '1.625rem',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        padding: '12px 16px',
                                        backgroundColor: '#fff',
                                        fontSize: '0.7rem',
                                        [`@media (min-width:${breakpoints.xl}px)`]: {
                                            fontSize: '1rem',
                                        },
                                        color: '#0f172a',
                                        lineHeight: '1.5rem',
                                        fontWeight: 400,
                                        transition: 'all 0.2s',
                                    },
                                    '& .MuiInputBase-input::placeholder': {
                                        color: '#64748b',
                                        opacity: 1,
                                        fontSize: '13px',
                                        lineHeight: '1rem',
                                        fontWeight: 400,
                                    },  
                                    '& .MuiFormHelperText-root': {
                                        fontSize: '0.7rem',
                                        lineHeight: '1rem',
                                        color: '#ef4444',
                                    },
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={handleTogglePasswordVisibility}
                                                edge="end"
                                            >
                                                {showPassword ? <EyeSlashIcon className="icon" /> : <EyeIcon className="icon" />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        )}
                    />
                </Box>
                <Box className={styles['remember-me-section']}>
                    <Box >
                    </Box>
                    <p
                        className={styles['forgot-password-link']}
                        onClick={onForgotPassword}
                        style={{ cursor: 'pointer', opacity: 1 }}
                    >
                        Forgot password?
                    </p>
                </Box>
                <Box className={styles['remember-me-section']}>
                    <p className={consentStyles.loginConsentNotice}>
                        {CONSENT_COPY.loginNoticePrefix}{' '}
                        <LegalDocumentLink to={DEFAULT_ROUTES.TERMS}>
                            Terms of Service
                        </LegalDocumentLink>{' '}
                        and{' '}
                        <LegalDocumentLink to={DEFAULT_ROUTES.PRIVACY}>
                            Privacy Policy
                        </LegalDocumentLink>
                        {CONSENT_COPY.loginNoticeSuffix}
                    </p>
                </Box>
                <Button
                    type="submit"
                    variant="primaryBtn"
                    fullWidth
                    disabled={loading}
                    className={styles['submit-button']}
                >
                    {loading ? (
                        <CircularProgress 
                            size={20} 
                            className={styles['submit-button-loading']}
                        />
                    ) : (
                        'Sign in'
                    )}
                </Button>
            </Box>
        </>
    );
};

export default LoginForm;
